import { rrulestr } from 'rrule';
import type { Event } from 'commoncal-shared';

export type ExpandedEvent = Event & {
  _instance?: boolean;
  _original_id?: string;
  _occurrence_date?: string;
};

/**
 * Resolve the event ID and occurrence date to use when editing an event.
 * For recurring instances, expanded events use a synthetic display ID; the
 * real event ID is stored in `_original_id`. Desktop and mobile must use this
 * helper so they both call the API with the same correct ID.
 */
export function resolveEventForEdit(event: ExpandedEvent): {
  eventId: string;
  occurrenceDate?: string;
} {
  return {
    eventId: event._original_id ?? event.id,
    occurrenceDate: event._occurrence_date,
  };
}

/**
 * Expand recurring events into individual occurrences for a date range.
 * Events with no rrule are returned as-is.
 * Events with rrule are expanded into multiple instances.
 * Exdates are respected.
 */
export function expandEvents(
  events: Event[],
  from: Date,
  to: Date
): ExpandedEvent[] {
  const result: ExpandedEvent[] = [];

  for (const event of events) {
    if (!event.rrule) {
      // Non-recurring event - include as-is
      result.push(event);
      continue;
    }

    try {
      // ── All-day events ────────────────────────────────────────────────
      // All-day events have no timezone; they use plain YYYY-MM-DD date
      // strings (start_date / end_date). Anchor DTSTART at noon local so
      // the rrule library stays on the correct calendar day regardless of
      // UTC offset.  Instances update start_date / end_date using the
      // occurrence's local date components directly — no UTC math needed.
      if (
        event.all_day &&
        event.start_date &&
        event.end_date
      ) {
        const exdates = new Set(
          event.exdate.map(d =>
            d.length === 10 ? d : d.slice(0, 10)
          )
        );

        const dtstart = new Date(
          event.start_date + 'T12:00'
        );
        const ruleStr = `DTSTART:${formatRRuleDate(dtstart)}\n${event.rrule}`;
        const rule = rrulestr(ruleStr);
        const occurrences = rule.between(from, to, true);

        // Duration in whole days
        const startD = new Date(
          event.start_date + 'T12:00'
        );
        const endD = new Date(event.end_date + 'T12:00');
        const durationDays = Math.round(
          (endD.getTime() - startD.getTime()) / 86400000
        );

        const pad = (n: number) =>
          String(n).padStart(2, '0');
        for (const occurrence of occurrences) {
          // occurrence has local date in its date fields (floating DTSTART)
          const occYYYYMMDD = `${occurrence.getFullYear()}-${pad(occurrence.getMonth() + 1)}-${pad(occurrence.getDate())}`;
          if (exdates.has(occYYYYMMDD)) continue;

          const instanceEndDate = new Date(
            occurrence.getFullYear(),
            occurrence.getMonth(),
            occurrence.getDate() + durationDays
          );
          const instanceEndYYYYMMDD = `${instanceEndDate.getFullYear()}-${pad(instanceEndDate.getMonth() + 1)}-${pad(instanceEndDate.getDate())}`;

          result.push({
            ...event,
            id: `${event.id}_${occYYYYMMDD}`,
            start_date: occYYYYMMDD,
            end_date: instanceEndYYYYMMDD,
            _instance: true,
            _original_id: event.id,
            _occurrence_date: occurrence.toISOString(),
          });
        }
        continue;
      }

      // ── Timed events ──────────────────────────────────────────────────
      // Timed events are stored in UTC (start_at / end_at ISO strings).
      // DTSTART is formatted as floating local time (no Z) so that the
      // rrule library keeps recurrences on the correct local calendar day.
      // The rrule lib stores floating times using UTC component fields
      // internally, so we use UTC getters to reconstruct the proper timestamp.

      // Parse exdates into Date objects for comparison
      const exdates = new Set(
        event.exdate.map(d => {
          const date = new Date(d);
          return new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate()
          ).getTime();
        })
      );

      const dtstart = new Date(event.start_at);
      const ruleStr = `DTSTART:${formatRRuleDate(dtstart)}\n${event.rrule}`;
      const rule = rrulestr(ruleStr);

      // Get all occurrences in the range
      const occurrences = rule.between(from, to, true);

      // Duration preserved from original event
      const originalStart = new Date(event.start_at);
      const originalEnd = new Date(event.end_at);
      const durationMs =
        originalEnd.getTime() - originalStart.getTime();

      for (const occurrence of occurrences) {
        // Check if this occurrence is excluded
        const occurrenceDate = new Date(
          occurrence.getFullYear(),
          occurrence.getMonth(),
          occurrence.getDate()
        );
        if (exdates.has(occurrenceDate.getTime())) {
          continue;
        }

        // FIX: rrule returns dates with UTC timestamps matching the intended LOCAL time.
        // For 5:15 PM PDT, rrule gives us a Date at 17:15 UTC (wrong timestamp).
        // We need to shift by the timezone offset to get correct UTC timestamp.
        // occurrence.getTime() = timestamp with local hour as UTC hour
        // ADD offset to shift from fake-UTC to real UTC timestamp
        const timezoneOffsetMs =
          occurrence.getTimezoneOffset() * 60000;
        const instanceStart = new Date(
          occurrence.getTime() + timezoneOffsetMs
        );
        const instanceEnd = new Date(
          instanceStart.getTime() + durationMs
        );

        result.push({
          ...event,
          id: `${event.id}_${occurrence.toISOString()}`, // Unique ID for each instance
          start_at: instanceStart.toISOString(),
          end_at: instanceEnd.toISOString(),
          _instance: true,
          _original_id: event.id,
          _occurrence_date: occurrence.toISOString(),
        });
      }
    } catch (err) {
      // If rrule parsing fails, fall back to showing the original event
      console.warn(
        'Failed to parse RRULE:',
        event.rrule,
        err
      );
      result.push(event);
    }
  }

  // Sort by start time
  return result.sort(
    (a, b) =>
      new Date(a.start_at).getTime() -
      new Date(b.start_at).getTime()
  );
}

/**
 * Format a date for RRULE DTSTART (YYYYMMDDTHHMMSS) — no Z suffix.
 * Uses local time components so the rrule library treats this as a
 * "floating" time, anchoring recurrence to the user's local calendar
 * day rather than the UTC day. This prevents a day-of-week shift in
 * negative UTC offset timezones (e.g. UTC-7).
 */
function formatRRuleDate(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}T${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
}

/**
 * Format an ISO date string for RRULE UNTIL (YYYYMMDDTHHMMSSZ)
 * UNTIL must be in UTC format with Z suffix per RRULE spec
 */
function formatRRuleUntilDate(isoDate: string): string {
  const date = new Date(isoDate);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(date.getUTCDate())}T${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}${pad(date.getUTCSeconds())}Z`;
}

/**
 * Generate an RRULE string from user-friendly options
 */
export type RecurrencePattern =
  | { type: 'none' }
  | { type: 'daily'; interval: number; until?: string }
  | {
      type: 'weekly';
      interval: number;
      days: number[];
      until?: string;
    } // 0=Sun, 1=Mon, ..., 6=Sat
  | {
      type: 'monthly';
      position: number;
      day: number;
      until?: string;
    } // position: 1-4, day: 0-6
  | {
      type: 'yearly';
      interval: number;
      until?: string;
    };

/**
 * Build an RRULE string from a pattern.
 * Returns null for 'none' type.
 */
export function buildRRule(
  pattern: RecurrencePattern
): string | null {
  switch (pattern.type) {
    case 'none':
      return null;

    case 'daily': {
      let rule = `RRULE:FREQ=DAILY;INTERVAL=${pattern.interval}`;
      if (pattern.until) {
        rule += `;UNTIL=${formatRRuleUntilDate(pattern.until)}`;
      }
      return rule;
    }

    case 'weekly': {
      const dayNames = [
        'SU',
        'MO',
        'TU',
        'WE',
        'TH',
        'FR',
        'SA',
      ];
      const byday = pattern.days
        .sort()
        .map(d => dayNames[d])
        .join(',');
      let rule = `RRULE:FREQ=WEEKLY;INTERVAL=${pattern.interval};BYDAY=${byday}`;
      if (pattern.until) {
        rule += `;UNTIL=${formatRRuleUntilDate(pattern.until)}`;
      }
      return rule;
    }

    case 'monthly': {
      const dayNames = [
        'SU',
        'MO',
        'TU',
        'WE',
        'TH',
        'FR',
        'SA',
      ];
      let rule = `RRULE:FREQ=MONTHLY;BYSETPOS=${pattern.position};BYDAY=${dayNames[pattern.day]}`;
      if (pattern.until) {
        rule += `;UNTIL=${formatRRuleUntilDate(pattern.until)}`;
      }
      return rule;
    }

    case 'yearly': {
      let rule = `RRULE:FREQ=YEARLY;INTERVAL=${pattern.interval}`;
      if (pattern.until) {
        rule += `;UNTIL=${formatRRuleUntilDate(pattern.until)}`;
      }
      return rule;
    }

    default:
      return null;
  }
}

/**
 * Parse an RRULE string into a user-friendly pattern.
 * Returns null if parsing fails.
 */
export function parseRRule(
  rrule: string
): RecurrencePattern | null {
  if (!rrule) return { type: 'none' };

  try {
    // Handle both "RRULE:..." and raw "FREQ=..." formats
    const content = rrule.startsWith('RRULE:')
      ? rrule.slice(6)
      : rrule;
    const parts = content.split(';');
    const params: Record<string, string> = {};

    for (const part of parts) {
      const [key, value] = part.split('=');
      if (key && value) {
        params[key] = value;
      }
    }

    const freq = params.FREQ;
    const interval = parseInt(params.INTERVAL || '1', 10);

    // Parse UNTIL if present - convert from YYYYMMDDTHHMMSSZ to ISO date
    let until: string | undefined;
    if (params.UNTIL) {
      const untilMatch = params.UNTIL.match(
        /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})(Z?)$/
      );
      if (untilMatch) {
        const [
          ,
          year,
          month,
          day,
          hour,
          minute,
          second,
          z,
        ] = untilMatch;
        if (z) {
          // UTC date - parse directly
          until = new Date(
            `${year}-${month}-${day}T${hour}:${minute}:${second}Z`
          ).toISOString();
        } else {
          // Local date - treat as local midnight
          until = new Date(
            `${year}-${month}-${day}T${hour}:${minute}:${second}`
          ).toISOString();
        }
      }
    }

    switch (freq) {
      case 'DAILY':
        return { type: 'daily', interval, until };

      case 'WEEKLY': {
        const byday = params.BYDAY || '';
        const dayNames = [
          'SU',
          'MO',
          'TU',
          'WE',
          'TH',
          'FR',
          'SA',
        ];
        const days = byday
          .split(',')
          .filter(Boolean)
          .map(d => dayNames.indexOf(d))
          .filter(d => d !== -1);
        return { type: 'weekly', interval, days, until };
      }

      case 'MONTHLY': {
        const bysetpos = parseInt(
          params.BYSETPOS || '1',
          10
        );
        const byday = params.BYDAY || 'MO';
        const dayNames = [
          'SU',
          'MO',
          'TU',
          'WE',
          'TH',
          'FR',
          'SA',
        ];
        const day = dayNames.indexOf(byday);
        return {
          type: 'monthly',
          position: bysetpos,
          day: day !== -1 ? day : 1,
          until,
        };
      }

      case 'YEARLY':
        return { type: 'yearly', interval, until };

      default:
        return null;
    }
  } catch {
    return null;
  }
}
